import { memo, useState } from "react";
import { useDispatch } from "react-redux";

import { Button } from "antd";
import { useChartsContext } from "contexts/ChartContext";
import { updateChartByPath } from "store/features";
import styled from "styled-components/macro";
import { removeCount } from "utils";

import { getWellDetailCard } from "api/data";

import { Uwi } from "models";
import { EntityKind } from "models/entityKind";

import ClipboardIcon from "components/icons/Clipboard";
import ClipboardCheckFilledIcon from "components/icons/ClipboardCheckFilled";

interface CopyEntityButtonProps {
  uwi: string;
  id: string;
}

const CopyUWIButton = ({ uwi, id }: CopyEntityButtonProps) => {
  const { entityKind } = useChartsContext();
  const dispatch = useDispatch();
  // sync Icon with copy status
  const [copied, setCopied] = useState(false);
  const [copiedUnformatted, setCopiedUnformatted] = useState(false);
  const [facilityName, setFacilityName] = useState<string | null>(null);

  const fetchFacilityName = async (facilityId: string): Promise<string> => {
    try {
      const response = await getWellDetailCard({
        uwid: facilityId,
        fields: ["Header.FacilityName"],
        entityKind: EntityKind.Facility
      });

      const facilityNameFromApi = response.data?.["Header.FacilityName"] || facilityId;
      setFacilityName(facilityNameFromApi);
      return facilityNameFromApi;
    } catch (error) {
      return facilityId;
    }
  };

  const handleClick = (useFormatted: boolean) => async () => {
    let valueToCopy = uwi;

    if (entityKind === EntityKind.Well) {
      const uwiConverter = new Uwi();
      valueToCopy = useFormatted
        ? uwiConverter.toFormatted(removeCount(uwi))
        : uwiConverter.toUnformatted(removeCount(uwi));
    } else if (entityKind === EntityKind.Facility) {
      if (useFormatted) {
        if (facilityName) {
          valueToCopy = facilityName;
        } else {
          valueToCopy = await fetchFacilityName(uwi);
        }
      } else {
        valueToCopy = uwi;
      }
    }

    navigator.clipboard.writeText(valueToCopy).then(() => {
      if (useFormatted) {
        setCopied(true);
      } else {
        setCopiedUnformatted(true);
      }
    });

    // hide context menu after 1 second
    setTimeout(() => {
      setCopied(false);
      setCopiedUnformatted(false);
      dispatch(
        updateChartByPath({
          id,
          path: `contextMenu.point`,
          value: null
        })
      );
    }, 1000);

    dispatch(
      updateChartByPath({
        id,
        path: `contextMenu.active`,
        value: false
      })
    );
  };

  const isWell = entityKind === EntityKind.Well;

  return (
    <>
      <StyledButton type="text" onClick={handleClick(true)}>
        {isWell ? "Copy UWI" : "Copy Facility Name"}
        {copied ? <ClipboardCheckFilledIcon /> : <ClipboardIcon />}
      </StyledButton>
      <StyledButton type="text" onClick={handleClick(false)}>
        {isWell ? "Copy Unformatted UWI" : "Copy Facility ID"}
        {copiedUnformatted ? <ClipboardCheckFilledIcon /> : <ClipboardIcon />}
      </StyledButton>
    </>
  );
};

export default memo(CopyUWIButton);

const StyledButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 12px;
`;
